import { useEffect, useState } from "react";
import styled from "styled-components";
import { getLocale } from "react-i18nify";

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Autocomplete,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";

import CloseIcon from "@mui/icons-material/Close";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";

import font from "src/styles/font";
import { Black, DarkGrey, Grey, Primary } from "src/styles/color";
import { checkNotEmpty } from "src/common";

import { deleteProductInGroup, updateProductGroup } from "src/api/deal";
import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

const WrapperTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.size.md};
  font-weight: 500;
  color: ${Black};
`;

const WrapperTextfield = styled.div`
  width: 100%;

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
    // text-align: center;
    // background: #fff;
  }
  .TextField-without-border-radius-amount fieldset {
    border-radius: 16px;
    width: 100%;
    text-align: center;
    background: #fff;
  }
  .MuiAutocomplete-root {
    margin: 0;
  }
  .MuiOutlinedInput-input {
    background: #fff;
    /* border-radius: 16px; */
    padding: 16px;
  }
  .MuiInputBase-root-MuiOutlinedInput-root {
    border-radius: 16px;
    background: #fff;
  }
  .MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root {
    border-radius: 16px;
  }

  .MuiTextField-root {
    border-radius: 16px;
  }
  .MuiOutlinedInput-root {
    border-radius: 16px;
  }
  .MuiInputAdornment-root {
    font-size: ${font.size.sm};
    color: ${Grey};
  }
  .MuiFormHelperText-root {
    margin-left: 0;
  }
  /* .MuiFormHelperText-root.Mui-error {
    color: ${DarkGrey};
  } */
`;

const LabelTextfield = styled.div`
  font-size: ${font.size.sm};
  font-weight: 400;
  color: ${Black};
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const TextOption = styled(Box)`
  width: 100%;
  color: ${({ color }) =>
    color === "grey"
      ? "var(--darkgrey-color)"
      : color === "primary"
        ? "var(--primary-color)"
        : "var(--secondary-color)"};
  font-size: ${font.size.sm};
  font-weight: 400;
  margin-bottom: 8px;
`;

const ProductBox = styled(Box)`
  width: 100%;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* justify-content: center; */
`;

const ListProduct = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
    color: ${Black};
  }
`;

const StyledInputAdornment = styled(InputAdornment)`
  margin: 0;

  &.MuiInputAdornment-root {
    font-size: ${font.size.sm};
    font-weight: 400;
    color: ${Grey};
  }
`;

const EditProductDialog = ({
  initialValue,
  initialGroup = null, // lock group to add
  dealGroups = [], // deal group list to select
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();

  const [formData, setFormData] = useState({
    group_id: !!initialValue?.group_id
      ? initialValue?.group_id
      : initialGroup || null,
    price: initialValue?.price || 0,
    special_price: initialValue?.special_price || null,
    product_choice_deal: initialValue?.product_choice_deal || null,
  });

  const isFormValid = ["group_id", "price"].every((required) => {
    let objVal = formData[required];
    // return objVal !== null && objVal !== 0;
    return checkNotEmpty(objVal);
  });

  const handleOnClose = () => {
    openDialog({
      type: "confirm",
      title: `คุณต้องการออกจากหน้านี้ใช่หรือไม่`,
      onConfirm: () => {
        onClose();
      },
      maxWidth: 342,
    });
  };

  const handleDelete = async () => {
    try {
      await deleteProductInGroup(
        formData?.group_id?._id,
        formData?.product_choice_deal?._id,
      );

      await enqueueSnackbar("ลบสินค้าสำเร็จ", {
        variant: "success",
      });

      await onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    try {
      await updateProductGroup({
        _id: formData?.group_id?._id,
        product_choice_deals: [
          {
            product_choice_deal: formData?.product_choice_deal?._id,
            price: formData?.price,
            special_price: formData?.special_price,
          },
        ],
      });

      await enqueueSnackbar("เพิ่มสินค้าสำเร็จ", {
        variant: "success",
      });

      await onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    // fetchProduct();
    if (!formData?.price) {
      setFormData({
        ...formData,
        price: formData.product_choice_deal?.group_price?.price,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  return (
    <Dialog
      // onClose={onClose}
      onClose={handleOnClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1024,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <WrapperTitle>สินค้า</WrapperTitle>

        <IconButton
          aria-label="close"
          // onClick={onClose}
          onClick={handleOnClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <ProductBox sx={{ mb: 3 }}>
          <ListProduct>
            <Box sx={{ width: 80 }}>
              {!!formData.product_choice_deal?.group_image
                ?.product_display_image === false ? (
                <ImageEmpty>
                  <InsertPhotoIcon
                    sx={{
                      color: "rgba(0, 0, 0, 0.54)",
                      cursor: "pointer",
                    }}
                  />
                </ImageEmpty>
              ) : (
                <img
                  style={{
                    width: "80px",
                    height: "80px",
                    objectFit: "cover",
                    objectPosition: "center",
                    borderRadius: "16px",
                    marginRight: "8px",
                  }}
                  src={
                    formData.product_choice_deal?.group_image
                      ?.product_display_image
                      ? formData.product_choice_deal?.group_image?.product_display_image.replace(
                          ".com",
                          ".com/300x300",
                        )
                      : ""
                  }
                  alt={
                    formData.product_choice_deal?.group_image
                      ?.product_display_image
                  }
                />
              )}
            </Box>

            <Box
              sx={{
                width: "18%",
                maxWidth: "200px",
              }}
            >
              <TextOption>
                {formData.product_choice_deal?.product?.item_name}
              </TextOption>

              <TextOption color="grey" sx={{ mb: 0 }}>
                {formData.product_choice_deal?.product?.item_code}
              </TextOption>
            </Box>

            <Box
              sx={{
                width: "17%",
                maxWidth: "200px",
                textAlign: "center",
              }}
            >
              <TextOption color="grey">
                {
                  formData.product_choice_deal?.product?.user?.seller
                    ?.company_name
                }
              </TextOption>
            </Box>

            <Box
              sx={{
                width: "18%",
                maxWidth: "200px",
              }}
            >
              <TextOption color="grey">
                {formData.product_choice_deal?.size?.quantity === "size"
                  ? `${formData.product_choice_deal?.size?.width} ${
                      getLocale() === "th"
                        ? formData.product_choice_deal?.size?.unit_width
                            ?.unit_th
                        : formData.product_choice_deal?.size?.unit_width
                            ?.unit_en
                    } x ${formData.product_choice_deal?.size?.height} ${
                      getLocale() === "th"
                        ? formData.product_choice_deal?.size?.unit_height
                            ?.unit_th
                        : formData.product_choice_deal?.size?.unit_height
                            ?.unit_en
                    } x ${formData.product_choice_deal?.size?.thickness} ${
                      getLocale() === "th"
                        ? formData.product_choice_deal?.size?.unit_thickness
                            ?.unit_th
                        : formData.product_choice_deal?.size?.unit_thickness
                            ?.unit_en
                    }`
                  : formData.product_choice_deal?.size?.quantity === "capacity"
                    ? `${formData.product_choice_deal?.size?.capacity_size} ${
                        getLocale() === "th"
                          ? formData.product_choice_deal?.size?.unit_capacity
                              ?.unit_th
                          : formData.product_choice_deal?.size?.unit_capacity
                              ?.unit_en
                      }`
                    : getLocale() === "th"
                      ? "สินค้าสั่งทำพิเศษ"
                      : "Made by order"}
              </TextOption>

              <TextOption color="grey" sx={{ mb: 0 }}>
                (
                {getLocale() === "th"
                  ? formData.product_choice_deal?.color?.color_th
                  : formData.product_choice_deal?.color?.color_en}
                )
              </TextOption>
            </Box>

            <Box
              sx={{
                width: "30%",
                maxWidth: "250px",
              }}
            >
              <Stack gap={1}>
                <Box>
                  <TextField
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.price}
                    sx={{
                      ".MuiOutlinedInput-input": {
                        px: 2,
                        py: 1,
                        textAlign: "right",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <StyledInputAdornment position="start">
                          <EditIcon fontSize="10" sx={{ color: Grey, mr: 1 }} />
                          ราคาปกติ
                        </StyledInputAdornment>
                      ),
                    }}
                    onChange={(e) => {
                      if (e.target.value * 0 === 0) {
                        setFormData({
                          ...formData,
                          price: e.target.value,
                        });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value * 0 === 0) {
                        setFormData({
                          ...formData,
                          price: parseFloat(e.target.value || 0),
                        });
                      }
                    }}
                    error={!formData.price || formData.price === 0}
                  />
                </Box>

                <Box>
                  <TextField
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.special_price}
                    sx={{
                      ".MuiOutlinedInput-input": {
                        px: 2,
                        py: 1,
                        textAlign: "right",
                        color: Primary,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <StyledInputAdornment position="start">
                          <EditIcon fontSize="10" sx={{ color: Grey, mr: 1 }} />
                          ราคาลด
                        </StyledInputAdornment>
                      ),
                    }}
                    onChange={(e) => {
                      if (e.target.value * 0 === 0) {
                        setFormData({
                          ...formData,
                          special_price: e.target.value,
                        });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value * 0 === 0) {
                        setFormData({
                          ...formData,
                          special_price: parseFloat(e.target.value) || null,
                        });
                      }
                    }}
                    // error={
                    //   !formData.special_price || formData.special_price === 0
                    // }
                  />
                </Box>
              </Stack>
            </Box>

            <Box>
              <IconButton
                aria-label="delete"
                onClick={() =>
                  openDialog({
                    type: "confirm",
                    title: `คุณต้องการลบสินค้าใช่หรือไม่`,
                    onConfirm: () => {
                      handleDelete();
                    },
                    maxWidth: 342,
                  })
                }
                sx={{
                  width: "30px",
                  p: "6px",
                  backgroundColor: "var(--darkwhite-color)",
                  ":disabled": {
                    ".MuiSvgIcon-root": {
                      color: "var(--darkgrey-color)",
                    },
                  },
                }}
              >
                <DeleteOutlineIcon
                  sx={{
                    color: "var(--black-color)",
                    width: "16px",
                    height: "18px",
                  }}
                />
              </IconButton>
            </Box>
          </ListProduct>
        </ProductBox>

        <Box>
          <Stack sx={{ mb: 0.5 }} flexDirection="row">
            <LabelTextfield>หมวดหมู่</LabelTextfield>
          </Stack>
          <WrapperTextfield>
            <Autocomplete
              // freeSolo
              aria-required={true}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              options={dealGroups}
              value={formData.group_id}
              onChange={(e, newValue) => {
                setFormData({
                  ...formData,
                  group_id: newValue,
                });
              }}
              getOptionLabel={(option) => {
                return option?.name;
              }}
              filterOptions={filterOptions}
              sx={{
                width: "100%",
              }}
              disabled={!checkNotEmpty(dealGroups) && !!initialGroup}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    maxWidth: "100%",
                    width: "100%",
                  }}
                  required
                  placeholder="โปรดหมวดหมู่"
                  error={!formData.group_id ? true : false}
                  helperText={"เลือกได้แค่ 1 หมวดหมู่เท่านั้น"}
                />
              )}
            />
          </WrapperTextfield>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          // onClick={onClose}
          onClick={handleOnClose}
          sx={{
            minWidth: 160,
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={!isFormValid}
          onClick={() => {
            openDialog({
              type: "confirm",
              title: `คุณต้องการบันทึกการแก้ไขใช่หรือไม่`,
              onConfirm: () => {
                handleSubmit();
              },
              maxWidth: 342,
            });
          }}
          sx={{
            minWidth: 160,
          }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProductDialog;
